import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClearNotificationsCache } from '@app/finxone-web-frontend/app/lib/actions/notification.action';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  NotificationPayloadDataResponse,
  NotificationsStatusRequest,
  ProfileStateModel,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { ListNotificationsResponse, Novu, NovuError } from '@novu/js';
import { BehaviorSubject, Observable, combineLatest, filter, take } from 'rxjs';
import { NotificationState, NotificationStateModel } from '../../state/notifications.state';
import { ProfileState } from '../../state/user-profile.state';
import { ConfigService } from '../config-service/config-service.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private applicationIdentifier = 'iq-5XbNQbPL_';
  private _loading = new BehaviorSubject<boolean>(true);
  public loading$ = this._loading.asObservable();

  public novuService: Novu;
  public novuServiceInitialised = false;

  @Select(ProfileState.getProfile)
  private profile$!: Observable<ProfileStateModel>;
  private baseUrl = '';

  constructor(
    private store: Store,
    private alertHandlerService: AlertHandlerService,
    private configService: ConfigService,
    private http: HttpClient,
  ) {
    this.configService
      .getApi('account_service')
      .pipe(
        filter((response) => !!response),
        take(1),
      )
      .subscribe((response) => {
        this.baseUrl = response;
      });
    this.setupNotifications();
  }

  public updateNotificationStatus(payload: NotificationsStatusRequest) {
    return this.http.post<NotificationsStatusRequest>(`${this.baseUrl}/notifications/markAs`, payload);
  }

  setupNotifications() {
    combineLatest([this.profile$, this.configService.getConfig()])
      .pipe(
        // profile id and activeOrganisationId must be present to initialize the notification service
        filter(([profile]) => (profile.id && profile.activeOrganisationId ? true : false)),
        take(1),
      )
      .subscribe(([profile, config]) => {
        this.clearNotifications();
        this.applicationIdentifier = config.novuApplicationIdentifier ?? 'iq-5XbNQbPL_';
        if (!profile.id || !profile.activeOrganisationId) {
          console.error(
            'NOTIFICATION setupNotifications Error: profile id or activeOrganisationId is not set',
          );
          return;
        }
        const subscriberId = profile.id + '_' + profile.activeOrganisationId;
        this.novuService = new Novu({
          applicationIdentifier: this.applicationIdentifier,
          subscriber: subscriberId,
        });

        this.novuService.on('session.initialize.resolved', () => {
          if (!this.novuServiceInitialised) {
            this.novuServiceInitialised = true;
          }
        });
      });
  }

  public async waitForNovuInitialization(timeoutMs = 3000): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (this.novuService) {
        // take early exit if service is already initialised to prevent registering duplicate listeners
        if (this.novuServiceInitialised) {
          resolve(true);
          return;
        }

        // Resolve to false in a given time out if session take too long to resolve
        setTimeout(() => {
          if (!this.novuServiceInitialised) {
            this.novuServiceInitialised = true;
            console.warn(`NOTIFICATION: Novu initialization failed`);
            resolve(false);
          } else {
            resolve(true);
          }
        }, timeoutMs);
      } else {
        resolve(false);
      }
    });
  }

  public getNotificationState(): Observable<NotificationStateModel> {
    return this.store.select(NotificationState.getNotification);
  }

  public callNovuServiceToFetchNotifications(
    pageNumber: number,
    limit: number,
    filterType = 'all',
  ): Promise<{
    data?: ListNotificationsResponse;
    error?: NovuError;
  }> {
    if (pageNumber !== undefined) {
      // pageNumber is defined, subtract 1 from it
      pageNumber -= 1;
    }
    if (pageNumber === undefined) {
      pageNumber = 0;
    }
    const config: { limit: number; offset: number; read?: boolean; useCache: boolean } = {
      limit: limit,
      offset: pageNumber * limit,
      useCache: false,
    };
    if (filterType !== 'all') config['read'] = filterType === 'read';
    if (this.novuServiceInitialised) {
      return this.novuService.notifications.list(config);
    } else {
      return this.waitForNovuInitialization().then((isInitialised) => {
        if (isInitialised) return this.novuService.notifications.list(config);
        else return { error: { message: 'Novu service initialization failed' } as NovuError };
      });
    }
  }

  public async getUnreadNotificationsCount(): Promise<number> {
    const fetchCount = () =>
      this.novuService.notifications._inboxService
        .count({ filters: [{ read: false }] })
        .then((response) => response?.data?.[0]?.count ?? 0)
        .catch((error: Error) => {
          console.error('Error while fetching unread notifications count', error);
          return 0;
        });

    if (this.novuServiceInitialised) return fetchCount();
    const isInitialised = await this.waitForNovuInitialization();
    return isInitialised ? fetchCount() : 0;
  }

  private clearNotifications() {
    this.store.dispatch(new ClearNotificationsCache());
  }

  public markNotificationAsRead(novuId: string): Observable<boolean> {
    return new Observable((observer) => {
      this.novuService.notifications
        .read({ notificationId: novuId })
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((error: Error) => {
          console.error('Error while marking notification as read', error);
          this.alertHandlerService.showAlertFn('error', 'Unable to mark the notification as read.');
          observer.error(error);
        });
    });
  }

  public markAllNotificationsAsRead(): Observable<boolean> {
    return new Observable((observer) => {
      this.novuService.notifications
        .readAll()
        .then(() => {
          observer.next();
          observer.complete();
        })
        .catch((error: Error) => {
          console.error('Error while marking all notifications as read', error);
          this.alertHandlerService.showAlertFn('error', 'Unable to mark all the notifications as read.');
          observer.error(error);
        });
    });
  }

  public fetchNotificationPayloadData(payload: {
    feedIdentifierIds: string[];
  }): Observable<NotificationPayloadDataResponse[]> {
    return this.http.post<NotificationPayloadDataResponse[]>(`${this.baseUrl}/notifications`, payload);
  }
}
